import { useFormik } from 'formik';
import { initialValues, validationSchema } from './formData';
import {
    Attachment,
    ISelect,
    ParamEnum,
} from '../../../../../../shared/types/shared';
import { useEffect, useState } from 'react';
import { PqrRedirect } from '../../../../../../shared/types/modules/pqr';
import { PqrRedirectProps } from './props';
import { asyncGetParams } from '../../../../../../services/param.service';
import { fileToBase64 } from '../../../../../../shared/utils/files';
import { GridColDef } from '@mui/x-data-grid';
import {
    PqrAttachmentActions,
    PqrAttachmentActionsLast,
} from '../../form/attachment/actions';
import { TableTooltip } from '../../../../../components/general/table';
import { DateTooltip } from '../../../../../components/general/dateTooltip';

export const usePqrRedirect = ({
    data,
    onChangeData,
    onChangeFormik,
}: PqrRedirectProps) => {
    const [areas, setAreas] = useState<ISelect[]>([]);
    const [person1, setPerson1] = useState<ISelect[]>([]);
    const [person2, setPerson2] = useState<ISelect[]>([]);
    const [person3, setPerson3] = useState<ISelect[]>([]);

    const onSubmit = (data: PqrRedirect) => {
        if (onChangeData) onChangeData(data || {});
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit,
    });

    const handleOnChange = async (files: File[]) => {
        const data: Attachment[] = [];
        for await (const file of files) {
            const res = await fileToBase64(file);
            if (res) data.push(res);
        }
        formik.setFieldValue('attachments', data);
    };

    const columns: GridColDef<Attachment>[] = [
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <PqrAttachmentActions attachment={row.row} />,
        },
        {
            field: 'name',
            headerName: 'Nombre',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => <TableTooltip value={row.row.name || ''} />,
        },
        {
            field: 'createdBy',
            headerName: 'Creado por',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={
                        row.row.createdBy
                            ? `(${row.row.createdBy.username}) ${row.row.createdBy.name} ${row.row.createdBy.surname}`
                            : ''
                    }
                />
            ),
        },
        {
            field: 'created_at',
            headerName: 'Fecha de creación',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <DateTooltip date={row.row.created_at} showFornow />
            ),
        },
        {
            field: 'link',
            headerName: 'Web link adjunto',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => (
                <PqrAttachmentActionsLast attachment={row.row} />
            ),
        },
    ];

    useEffect(() => {
        if (onChangeFormik) onChangeFormik(formik);
    }, [formik.values, formik.errors]);

    useEffect(() => {
        formik?.setFieldValue('area1', data?.area1 || '');
        formik?.setFieldValue('person1', data?.person1 || '');
        formik?.setFieldValue('email1', data?.email1 || '');
        formik?.setFieldValue('area2', data?.area2 || '');
        formik?.setFieldValue('person2', data?.person2 || '');
        formik?.setFieldValue('email2', data?.email2 || '');
        formik?.setFieldValue('area3', data?.area3 || '');
        formik?.setFieldValue('person3', data?.person3 || '');
        formik?.setFieldValue('email3', data?.email3 || '');
        formik?.setFieldValue('redirectPqr', data?.redirectPqr || false);
        formik?.setFieldValue('generateAlert', data?.generateAlert || false);
        formik?.setFieldValue('addAnnex', data?.addAnnex || false);
    }, [data]);

    useEffect(() => {
        if (Boolean(formik.values.area1)) {
            asyncGetParams(
                ParamEnum.PQR_REDIRECTION_PERSON,
                undefined,
                formik.values.area1
            ).then((items) => setPerson1(items));
        }
        if (!data || !Boolean(data.person1)) {
            formik?.setFieldValue('person1', '');
            formik?.setFieldValue('email1', '');
        }
    }, [formik.values.area1]);

    useEffect(() => {
        if (Boolean(formik.values.area2)) {
            asyncGetParams(
                ParamEnum.PQR_REDIRECTION_PERSON,
                undefined,
                formik.values.area2
            ).then((items) => setPerson2(items));
        }
        if (!data || !Boolean(data.person1)) {
            formik?.setFieldValue('person2', '');
            formik?.setFieldValue('email2', '');
        }
    }, [formik.values.area2]);

    useEffect(() => {
        if (Boolean(formik.values.area3)) {
            asyncGetParams(
                ParamEnum.PQR_REDIRECTION_PERSON,
                undefined,
                formik.values.area3
            ).then((items) => setPerson3(items));
        }
        if (!data || !Boolean(data.person3)) {
            formik?.setFieldValue('person3', '');
            formik?.setFieldValue('email3', '');
        }
    }, [formik.values.area3]);

    useEffect(() => {
        if (Boolean(formik.values.person1) && person1 && person1.length > 0) {
            const param = person1.find(
                (p) => p.id === formik.values.person1.toString()
            );
            if (param) formik?.setFieldValue('email1', param.extra);
        }
    }, [formik.values.person1]);

    useEffect(() => {
        if (Boolean(formik.values.person2) && person2 && person2.length > 0) {
            const param = person2.find(
                (p) => p.id === formik.values.person2.toString()
            );
            if (param) formik?.setFieldValue('email2', param.extra);
        }
    }, [formik.values.person2]);

    useEffect(() => {
        if (Boolean(formik.values.person3) && person3 && person3.length > 0) {
            const param = person3.find(
                (p) => p.id === formik.values.person3.toString()
            );
            if (param) formik?.setFieldValue('email3', param.extra);
        }
    }, [formik.values.person3]);

    useEffect(() => {
        asyncGetParams(ParamEnum.PQR_REDIRECTION_AREA).then((items) =>
            setAreas(items)
        );
    }, []);

    return {
        formik,
        areas,
        person1,
        person2,
        person3,
        handleOnChange,
        columns,
        rows: data?.attachments,
    };
};
